import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  Form,
  Button,
  FreeForm,
  FormItem,
  FormPage,
  Select,
  InputNumber,
} from 'iglooform';
import { useRequest } from 'ahooks';
import { history, useLocation } from 'umi';
import classnames from 'classnames';
import { getSession } from '@/utils/common';
import { CreateQuote, QuotationConfirm } from '../service';
import useMobile from '@/hooks/useMobile';
import useFormItemReaction from './hooks/useFormItemReaction';
import * as SESSION_KEYS from '@/configs/session-storage-keys';

import './index.less';

interface Props {
  nextStep: string;
  productType: string;
  needCreateQuote?: boolean;
  changeActiveStep: any;
  isBuyNowFlow?: boolean;
}

const TravelInfo = (props: Props) => {
  const {
    changeActiveStep,
    nextStep,
    needCreateQuote,
    productType,
    isBuyNowFlow = false,
  } = props;
  const isMobile = useMobile();
  const location: any = useLocation();
  const planPicked = getSession(SESSION_KEYS.PICKED_PLAN_INFO);
  const initFirstStepInfo = getSession(SESSION_KEYS.FIRST_STEP_INFO_FAST_QUOTE);

  const { formatMessage } = useIntl();
  const form = Form.useForm();

  const {
    continueButtonDisalbed,
    onChangeFormItem,
    visibilities,
    durationOptions,
    isBSHHc,
  } = useFormItemReaction(initFirstStepInfo);

  const { run: quotationConfirm } = useRequest(QuotationConfirm, {
    manual: true,
    onSuccess: () => {
      const quoteInfo = JSON.parse(
        sessionStorage.getItem(SESSION_KEYS.QUOTE_INFO) ?? '{}',
      );
      const historyJumpFunc = isBuyNowFlow ? history.push : history.replace;
      historyJumpFunc({
        pathname: `/home/${productType}/application`,
        query: {
          productKey: planPicked?.productKey,
          planKey: planPicked?.planKey,
          quotationId: quoteInfo?.id,
          goBackUrl: location.pathname + '?' + location.search,
          isBuyNowFlow: String(isBuyNowFlow),
        },
      });

      //event tracking: start_application
      window.gtag('event', 'start_application', {
        page: 'direct purchase',
        product_key: planPicked?.productKey,
        plan_key: planPicked?.planKey,
        quote_id: quoteInfo?.id,
        user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')
          ?.id,
      });
    },
  });

  const { run: createQuote } = useRequest(CreateQuote, {
    manual: true,
    onSuccess: (res) => {
      sessionStorage.setItem(SESSION_KEYS.QUOTE_INFO, JSON.stringify(res));
      if (nextStep === 'application') {
        quotationConfirm(
          productType,
          planPicked?.productKey,
          planPicked?.planKey,
          res?.id,
        );
      } else {
        changeActiveStep(nextStep);
      }
    },
  });

  useEffect(() => {
    if (isBSHHc) {
      form.setFieldValue(['hcInfo', 'insured_num'], 1);
      onChangeFormItem('insured_num', 1);
    }
  }, [isBSHHc]);

  return (
    <div className={classnames(['vehicleForm', { 'is-mobile': isMobile }])}>
      <FreeForm
        className="formContainer"
        form={form}
        showSubmitButton={false}
        initialValues={{
          hcInfo: {
            initFirstStepInfo,
          },
        }}
      >
        <FormPage name="hcInfo">
          {visibilities.policyDuration && (
            <FormItem
              name="policy_duration"
              label={formatMessage({
                id: 'Policy Duration',
              })}
            >
              <Select
                className="vehicleFormSelectVehicle"
                placeholder={formatMessage({ id: 'Make A Selection' })}
                onChange={(value) => onChangeFormItem('policy_duration', value)}
              >
                {durationOptions.map((e) => (
                  <Select.Option key={e.value} value={e.value}>
                    {e.label}
                  </Select.Option>
                ))}
              </Select>
            </FormItem>
          )}
          {visibilities.insuredNum && (
            <FormItem
              name="insured_num"
              label={formatMessage({
                id: 'No. of Insured',
              })}
              rules={[
                {
                  validator: (_, value) =>
                    new Promise((resolve, reject) => {
                      if (
                        (Number.isInteger(value) && value > 0 && value <= 20) ||
                        typeof value === 'undefined'
                      ) {
                        resolve('ok');
                      } else {
                        reject(
                          new Error(
                            formatMessage({
                              id: 'No. of Insured must be between 1 - 20',
                            }),
                          ),
                        );
                      }
                    }),
                },
              ]}
            >
              <InputNumber
                onChange={(event) =>
                  onChangeFormItem('insured_num', event.target.value)
                }
              />
            </FormItem>
          )}
        </FormPage>
      </FreeForm>

      <Button
        className={'continueButton'}
        type="primary"
        disabled={continueButtonDisalbed}
        onClick={() => {
          form.validateFields().then((value) => {
            sessionStorage.setItem(
              SESSION_KEYS.FIRST_STEP_INFO_FAST_QUOTE,
              JSON.stringify(value.hcInfo),
            );
            if (needCreateQuote) {
              const hcInfoJSON = value.hcInfo;
              if (isBSHHc) {
                hcInfoJSON.policy_duration = '12 Months';
              }
              if (location?.query?.button === 'buyNow') {
                hcInfoJSON.process_for_buy_now = true;
              }
              createQuote(
                productType,
                planPicked?.productKey,
                planPicked?.planKey,
                hcInfoJSON,
              );
            } else {
              changeActiveStep(nextStep);
            }
          });
        }}
      >
        {formatMessage({ id: 'Next' })}
      </Button>
    </div>
  );
};

export default TravelInfo;
